import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { m as motion, useReducedMotion } from "framer-motion";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, validateResetToken, resetPassword } from "@/lib/api";
import { TurnstileField } from "@/components/TurnstileField";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const mode = searchParams.get("mode") || "reset";
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const [form, setForm] = React.useState({
    valid: null as boolean | null,
    password: "",
    confirm: "",
    error: "",
    success: false,
    submitting: false,
    turnstileToken: "",
    turnstileResetKey: 0,
  });
  const fp = useDeviceFingerprint();
  const { valid, password, confirm, error, success, submitting, turnstileToken, turnstileResetKey } = form;
  const updateForm = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  React.useEffect(() => {
    if (!token) {
      updateForm({ valid: false });
      return;
    }
    validateResetToken(token)
      .then((res) => updateForm({ valid: res.valid }))
      .catch(() => updateForm({ valid: false }));
  }, [token]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    updateForm({ error: "" });
    if (password !== confirm) {
      updateForm({ error: "Passwords do not match." });
      return;
    }
    if (password.length < 8) {
      updateForm({ error: "Password must be at least 8 characters." });
      return;
    }
    updateForm({ submitting: true });
    try {
      await resetPassword({ token, new_password: password, turnstile_token: turnstileToken, device_fingerprint: fp.fp || undefined, client_metadata: fp.meta });
      updateForm({ success: true });
    } catch (err) {
      updateForm({ error: err instanceof ApiError ? err.message : "Request failed." });
      updateForm({ turnstileToken: "", turnstileResetKey: turnstileResetKey + 1 });
    } finally {
      updateForm({ submitting: false });
    }
  }

  if (valid === null) {
    return (
      <main className="grid min-h-screen place-items-center px-4">
        <div className="text-sm text-muted-foreground">Verifying link…</div>
      </main>
    );
  }

  if (valid === false) {
    return (
      <main className="grid min-h-screen place-items-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid or Expired Link</CardTitle>
            <CardDescription>
              This link is no longer valid. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/forgot-password">Request New Link</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (success) {
    return (
      <main className="grid min-h-screen place-items-center px-4">
        <motion.div className="w-full max-w-md" initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle>{mode === "invite" ? "Password Set" : "Password Updated"}</CardTitle>
              <CardDescription>
                {mode === "invite"
                  ? "Your account is now active. You can sign in with your new password."
                  : "Your password has been updated. You can now sign in."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate("/login")}>
                Sign In
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="grid min-h-screen place-items-center px-4 py-8">
      <motion.section
        className="w-full max-w-md"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>{mode === "invite" ? "Set Your Password" : "Reset Password"}</CardTitle>
            <CardDescription>
              {mode === "invite"
                ? "Choose a password to activate your account."
                : "Choose a new password for your account."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4" onSubmit={handleSubmit}>
              {error && (
                <Alert className="border-destructive/20 bg-destructive/10 text-destructive">
                  <div className="mb-1 font-medium leading-none">Error</div>
                  <div className="text-sm">{error}</div>
                </Alert>
              )}
              <div className="grid gap-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => updateForm({ password: e.target.value })}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => updateForm({ confirm: e.target.value })}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </div>
              <TurnstileField value={turnstileToken} onChange={(v) => updateForm({ turnstileToken: v })} resetKey={turnstileResetKey} />

              <Button type="submit" disabled={submitting || !turnstileToken}>
                {submitting ? "Saving…" : mode === "invite" ? "Activate Account" : "Update Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.section>
    </main>
  );
}
